// src/pages/UserManagement.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy, getDoc } from 'firebase/firestore';
import { useUserRole } from '../hooks/useUserRole';
import Navigation from '../components/Navigation';
import { UserRole } from '../hooks/useUserRole';
import { logDataModification, createAuditLog } from '../lib/audit/auditLogger';

interface User {
  id: string;
  email: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  role: UserRole;
  createdAt?: any;
  updatedAt?: any;
  profilePictureUrl?: string;
}

const UserManagement: React.FC = () => {
  const { user: currentUser, role, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'admin' | 'staff' | 'client' | 'all'>('all');
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [newRole, setNewRole] = useState<'admin' | 'staff' | 'client'>('client');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');

  useEffect(() => {
    if (!roleLoading && role !== 'admin') {
      navigate('/admin-dashboard');
    }
  }, [roleLoading, role, navigate]);

  useEffect(() => {
    if (role === 'admin') {
      loadUsers();
    }
  }, [role]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, 'users');
      const usersQuery = query(usersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(usersQuery);
      
      const usersData: User[] = [];
      snapshot.forEach((doc) => {
        usersData.push({
          id: doc.id,
          ...doc.data()
        } as User);
      });
      
      setUsers(usersData);
    } catch (error: any) {
      console.error('Error loading users:', error);
      setError('Failed to load users: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRoleValue: 'admin' | 'staff' | 'client') => {
    if (!window.confirm(`Are you sure you want to change this user's role to ${newRoleValue}?`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      // Get current user info for logging
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      const oldRole = userData?.role || 'client';
      
      await updateDoc(userRef, {
        role: newRoleValue,
        updatedAt: new Date()
      });

      // Log role change
      if (currentUser && currentUser.email && role) {
        await createAuditLog({
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userRole: role as 'admin' | 'staff' | 'client',
          eventType: 'role_changed',
          severity: 'warning',
          resourceType: 'user',
          resourceId: userId,
          description: `User role changed: ${userData?.email || userId} from ${oldRole} to ${newRoleValue}`,
          metadata: { oldRole, newRole: newRoleValue, targetUserId: userId, targetUserEmail: userData?.email }
        });
      }

      setSuccess(`User role updated to ${newRoleValue} successfully!`);
      setEditingUserId(null);
      await loadUsers();
    } catch (error: any) {
      console.error('Error updating user role:', error);
      setError('Failed to update user role: ' + error.message);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (!window.confirm(`Are you sure you want to delete user ${userEmail}? This action cannot be undone.`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      // Get user info before deletion for logging
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      
      await deleteDoc(userRef);

      // Log user deletion
      if (currentUser && currentUser.email && role) {
        await logDataModification(
          currentUser.uid,
          currentUser.email,
          role as 'admin' | 'staff' | 'client',
          'user_deleted',
          'user',
          userId,
          `User deleted: ${userEmail}`,
          { deletedUserRole: userData?.role, deletedUserEmail: userEmail }
        );
      }

      setSuccess('User deleted successfully!');
      await loadUsers();
      setSelectedUser(null);
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user: ' + error.message);
    }
  };

  const handleEditProfile = () => {
    if (selectedUser) {
      setEditFirstName(selectedUser.firstName || '');
      setEditLastName(selectedUser.lastName || '');
      setEditEmail(selectedUser.email || '');
      setIsEditingProfile(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    setEditFirstName('');
    setEditLastName('');
    setEditEmail('');
  };

  const handleSaveProfile = async () => {
    if (!selectedUser) return;

    try {
      setError('');
      setSuccess('');
      
      const displayName = `${editFirstName.trim()} ${editLastName.trim()}`.trim();
      const userRef = doc(db, 'users', selectedUser.id);
      
      await updateDoc(userRef, {
        firstName: editFirstName.trim(),
        lastName: editLastName.trim(),
        displayName: displayName,
        email: editEmail.trim(),
        updatedAt: new Date()
      });

      // Log profile update
      if (currentUser && currentUser.email && role) {
        logDataModification(
          currentUser.uid,
          currentUser.email,
          role as 'admin' | 'staff' | 'client',
          'user_updated',
          'user',
          selectedUser.id,
          `User profile updated: ${selectedUser.email}`,
          { updatedFields: ['firstName', 'lastName', 'displayName', 'email'] }
        ).catch((err: any) => console.error('Failed to log profile update:', err));
      }

      setSuccess('User profile updated successfully!');
      setIsEditingProfile(false);
      await loadUsers();
      
      // Update selected user to reflect changes
      const updatedUser = users.find(u => u.id === selectedUser.id);
      if (updatedUser) {
        setSelectedUser({
          ...updatedUser,
          firstName: editFirstName.trim(),
          lastName: editLastName.trim(),
          displayName: displayName,
          email: editEmail.trim()
        });
      }
    } catch (error: any) {
      console.error('Error updating user profile:', error);
      setError('Failed to update user profile: ' + error.message);
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch = 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.displayName && user.displayName.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const userRole = user.role || 'client'; // Default to 'client' if null
    const matchesRole = roleFilter === 'all' || userRole === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  const stats = {
    total: users.length,
    admin: users.filter(u => u.role === 'admin').length,
    staff: users.filter(u => u.role === 'staff').length,
    client: users.filter(u => u.role === 'client' || !u.role).length
  };

  if (roleLoading || loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (role !== 'admin') {
    return null;
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1>User Management</h1>
          <button
            onClick={() => navigate('/admin-dashboard')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Statistics Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <StatCard title="Total Users" value={stats.total} color="#007bff" />
          <StatCard title="Admins" value={stats.admin} color="#dc3545" />
          <StatCard title="Staff" value={stats.staff} color="#ffc107" />
          <StatCard title="Clients" value={stats.client} color="#28a745" />
        </div>

        {/* Alerts */}
        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #c3e6cb'
          }}>
            {success}
          </div>
        )}

        {/* Filters */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Search Users
            </label>
            <input
              type="text"
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '1rem'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Filter by Role
            </label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as 'admin' | 'staff' | 'client' | 'all')}
              style={{
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="staff">Staff</option>
              <option value="client">Client</option>
            </select>
          </div>
        </div>

        {/* Users Table and Details */}
        <div style={{ display: 'grid', gridTemplateColumns: selectedUser ? '1fr 1fr' : '1fr', gap: '2rem' }}>
        {/* Users Table */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>User</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Email</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Role</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Created</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                    {loading ? 'Loading users...' : 'No users found'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr 
                    key={user.id}
                    onClick={() => setSelectedUser(user)}
                    style={{ 
                      borderBottom: '1px solid #dee2e6',
                      backgroundColor: selectedUser?.id === user.id ? '#e7f3ff' : 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {user.profilePictureUrl ? (
                          <img
                            src={user.profilePictureUrl}
                            alt={user.displayName || user.email}
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#e9ecef',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#6c757d',
                            fontWeight: 'bold'
                          }}>
                            {(user.displayName || user.email).charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: '500' }}>
                            {user.firstName && user.lastName 
                              ? `${user.firstName} ${user.lastName}`
                              : (user.displayName && user.displayName.trim() 
                                ? user.displayName 
                                : user.email.split('@')[0])}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>{user.email}</td>
                    <td style={{ padding: '1rem' }}>
                      {editingUserId === user.id ? (
                        <select
                          value={newRole}
                          onChange={(e) => setNewRole(e.target.value as 'admin' | 'staff' | 'client')}
                          style={{
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            fontSize: '0.875rem'
                          }}
                        >
                          <option value="admin">Admin</option>
                          <option value="staff">Staff</option>
                          <option value="client">Client</option>
                        </select>
                      ) : (
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          backgroundColor: 
                            user.role === 'admin' ? '#dc3545' :
                            user.role === 'staff' ? '#ffc107' : '#28a745',
                          color: user.role === 'staff' ? '#000' : '#fff'
                        }}>
                          {user.role || 'client'}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '1rem', color: '#6c757d', fontSize: '0.875rem' }}>
                      {user.createdAt?.toDate ? user.createdAt.toDate().toLocaleDateString() : 'N/A'}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        {editingUserId === user.id ? (
                          <>
                            <button
                              onClick={() => {
                                handleRoleChange(user.id, newRole);
                              }}
                              style={{
                                padding: '0.25rem 0.75rem',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.875rem'
                              }}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingUserId(null);
                                setNewRole('client');
                              }}
                              style={{
                                padding: '0.25rem 0.75rem',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.875rem'
                              }}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            {user.id !== currentUser?.uid && (
                              <>
                                <button
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click when clicking button
                                setEditingUserId(user.id);
                                setNewRole((user.role || 'client') as 'admin' | 'staff' | 'client');
                              }}
                                  style={{
                                    padding: '0.25rem 0.75rem',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  Edit Role
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteUser(user.id, user.email);
                                  }}
                                  style={{
                                    padding: '0.25rem 0.75rem',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* User Details Panel */}
        {selectedUser && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            padding: '2rem',
            position: 'sticky',
            top: '2rem',
            maxHeight: 'calc(100vh - 4rem)',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>User Details</h2>
              <button
                onClick={() => {
                  setSelectedUser(null);
                  setIsEditingProfile(false);
                  setEditFirstName('');
                  setEditLastName('');
                  setEditEmail('');
                }}
                style={{
                  padding: '0.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>

            {/* Profile Picture */}
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              {selectedUser.profilePictureUrl ? (
                <img
                  src={selectedUser.profilePictureUrl}
                  alt={selectedUser.firstName && selectedUser.lastName 
                    ? `${selectedUser.firstName} ${selectedUser.lastName}`
                    : (selectedUser.displayName || selectedUser.email)}
                  style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid #007bff'
                  }}
                />
              ) : (
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  backgroundColor: '#e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6c757d',
                  fontWeight: 'bold',
                  fontSize: '2rem',
                  margin: '0 auto',
                  border: '3px solid #007bff'
                }}>
                  {(selectedUser.firstName && selectedUser.lastName
                    ? `${selectedUser.firstName} ${selectedUser.lastName}`
                    : (selectedUser.displayName || selectedUser.email)).charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* User Information */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, color: '#495057' }}>User Information</h3>
                {!isEditingProfile && (
                  <button
                    onClick={handleEditProfile}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Edit Profile
                  </button>
                )}
              </div>
              
              {isEditingProfile ? (
                <div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
                      First Name
                    </label>
                    <input
                      type="text"
                      value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={editLastName}
                      onChange={(e) => setEditLastName(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                    <button
                      onClick={handleSaveProfile}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <UserDetailRow label="Name" 
                    value={
                      selectedUser.firstName && selectedUser.lastName 
                        ? `${selectedUser.firstName} ${selectedUser.lastName}`
                        : (selectedUser.displayName && selectedUser.displayName.trim() 
                          ? selectedUser.displayName 
                          : selectedUser.email.split('@')[0] || 'Not set')
                    } 
                  />
                  {selectedUser.firstName && selectedUser.lastName && (
                    <>
                      <UserDetailRow label="First Name" value={selectedUser.firstName} />
                      <UserDetailRow label="Last Name" value={selectedUser.lastName} />
                    </>
                  )}
                  <UserDetailRow label="Email" value={selectedUser.email} />
                  <UserDetailRow label="User ID" value={selectedUser.id} />
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
                      Role
                    </div>
                    <div>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        backgroundColor: 
                          selectedUser.role === 'admin' ? '#dc3545' :
                          selectedUser.role === 'staff' ? '#ffc107' : '#28a745',
                        color: selectedUser.role === 'staff' ? '#000' : '#fff'
                      }}>
                        {selectedUser.role || 'client'}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Timestamps */}
            {(selectedUser.createdAt || selectedUser.updatedAt) && (
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #dee2e6' }}>
                {selectedUser.createdAt && (
                  <UserDetailRow 
                    label="Created" 
                    value={selectedUser.createdAt?.toDate ? selectedUser.createdAt.toDate().toLocaleString() : 'N/A'} 
                  />
                )}
                {selectedUser.updatedAt && (
                  <UserDetailRow 
                    label="Last Updated" 
                    value={selectedUser.updatedAt?.toDate ? selectedUser.updatedAt.toDate().toLocaleString() : 'N/A'} 
                  />
                )}
              </div>
            )}

            {/* Actions */}
            {selectedUser.id !== currentUser?.uid && !isEditingProfile && (
              <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #dee2e6' }}>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#495057' }}>Actions</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <button
                    onClick={() => {
                      setEditingUserId(selectedUser.id);
                      setNewRole((selectedUser.role || 'client') as 'admin' | 'staff' | 'client');
                    }}
                    style={{
                      padding: '0.75rem',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    Edit Role
                  </button>
                  <button
                    onClick={() => handleDeleteUser(selectedUser.id, selectedUser.email)}
                    style={{
                      padding: '0.75rem',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    Delete User
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        </div>

        {/* Info Box */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: '#e7f3ff',
          borderRadius: '4px',
          border: '1px solid #b3d9ff'
        }}>
          <strong>Note:</strong> Only admins can change user roles. Deleting a user will remove their profile from the system but not their Firebase Authentication account. Click on a user row to view details.
        </div>
      </div>
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: number;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, color }) => (
  <div style={{
    backgroundColor: 'white',
    padding: '1rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderLeft: `4px solid ${color}`
  }}>
    <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.5rem' }}>
      {title}
    </div>
    <div style={{ fontSize: '2rem', fontWeight: 'bold', color }}>
      {value}
    </div>
  </div>
);

interface UserDetailRowProps {
  label: string;
  value: string;
}

const UserDetailRow: React.FC<UserDetailRowProps> = ({ label, value }) => (
  <div style={{ marginBottom: '0.75rem' }}>
    <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
      {label}
    </div>
    <div style={{ fontSize: '1rem', fontWeight: '500', wordBreak: 'break-all' }}>
      {value}
    </div>
  </div>
);

export default UserManagement;

